/**
 * <mh-countdown> — MommyHana flash-sale countdown.
 *
 * Vanilla custom element, no dependencies, no library (SPEC-PHASE0 §3.4).
 *
 * The contract this element enforces (SPEC-PHASE0 §5, S6):
 *
 *   - It counts down to the deadline in `data-end-ms` and to nothing else. That value is a fixed
 *     epoch resolved server-side from the `end_time` setting. The element never reads the clock to
 *     establish its target, only to measure the distance to it, so a refresh cannot move the
 *     deadline and the timer cannot restart or loop.
 *   - If the deadline is missing, malformed or already past, the element hides itself and stops.
 *     A deadline that resets on refresh is a false claim about a sale ending.
 *
 * The section already refuses to emit this element unless the deadline parsed and is in the
 * future, so this is the second of two guards, covering the case where a page was cached before
 * the sale ended and is being viewed after.
 *
 * ---------------------------------------------------------------------------------------------
 * OPT-IN: `data-ended-mode="message"` (added for the coming-soon template, E2a)
 *
 * A flash sale that has ended must disappear, which is why the default is to remove the element.
 * A coming-soon page is the opposite case: when the clock reaches zero the visitor is supposed to
 * be told the event has started, and must never see a negative number. Both behaviours are the
 * same countdown, so rather than fork one, this element takes an opt-in:
 *
 *   <mh-countdown data-end-ms="..." data-ended-mode="message">
 *     <div data-countdown-units> ... [data-unit="days"] etc ... </div>
 *     <p data-countdown-ended hidden>Acara telah bermula</p>
 *     <p class="visually-hidden" data-countdown-announce aria-live="polite"></p>
 *   </mh-countdown>
 *
 * With that attribute set, reaching zero hides the units and reveals `[data-countdown-ended]`
 * instead of removing the element. WITHOUT it, every path behaves exactly as before, which is
 * what keeps the two live flash templates untouched.
 *
 * A malformed or missing `data-end-ms` still retires the element even in message mode: a typo in
 * the setting must not announce that an event has started. The section guards that server-side
 * too, so the element is only emitted with an epoch that parsed.
 *
 * ---------------------------------------------------------------------------------------------
 * OPT-IN: `data-pad="off"` (added for the Neelofa ad hero)
 *
 * The digits are zero-padded to two places by default, which is what a boxed HARI / JAM / MINIT /
 * SAAT display wants: the boxes stay the same width as the numbers shrink. A countdown written as
 * a sentence wants the opposite, because "07 hari 16 jam" is not how the number is spoken.
 *
 * `data-pad="off"` writes the plain number instead. Every existing caller omits the attribute and
 * is padded exactly as before; only the element that asks for it changes.
 * ---------------------------------------------------------------------------------------------
 *
 * `[data-countdown-announce]` is optional and polite. It is written once a MINUTE rather than
 * once a second, because a live region that changes every second is unusable with a screen reader
 * on. See docs/DECISIONS.md D94.
 * ---------------------------------------------------------------------------------------------
 */

class MhCountdown extends HTMLElement {
  #timer = null;
  #observer = null;
  #endMs = 0;
  #lastAnnouncedMinute = null;

  connectedCallback() {
    this.#endMs = Number.parseInt(this.dataset.endMs ?? '', 10);

    if (!Number.isFinite(this.#endMs) || this.#endMs <= 0) {
      this.#retire();
      return;
    }

    // Lazy start: nothing ticks until the element is actually on screen. An expired deadline is
    // still caught immediately, because a sale that has ended must not look live even off-screen.
    if (this.#remaining() <= 0) {
      this.#finish();
      return;
    }

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#start();
            return;
          }
        }
      });
      this.#observer.observe(this);
    } else {
      this.#start();
    }
  }

  disconnectedCallback() {
    this.#stop();
  }

  #remaining() {
    return this.#endMs - Date.now();
  }

  #start() {
    if (this.#timer !== null) return;
    this.#render();
    this.#timer = window.setInterval(() => this.#render(), 1000);
  }

  #stop() {
    if (this.#timer !== null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
    if (this.#observer !== null) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  /**
   * What to do when the deadline is reached: the default is to disappear, and `message` mode
   * swaps the units for the "it has started" line instead. Everything upstream calls this rather
   * than `#retire()` directly, so the choice lives in exactly one place.
   */
  #finish() {
    if (this.dataset.endedMode === 'message' && this.querySelector('[data-countdown-ended]')) {
      this.#showEnded();
      return;
    }
    this.#retire();
  }

  /** Message mode: stop, hide the digits, reveal the line that says the event has started. */
  #showEnded() {
    this.#stop();

    const units = this.querySelector('[data-countdown-units]');
    if (units) units.hidden = true;

    const ended = this.querySelector('[data-countdown-ended]');
    if (ended) {
      ended.hidden = false;
      this.#announce(ended.textContent.trim());
    }
  }

  /**
   * The polite live region, written only when the text actually changes. Assigning the same string
   * again would make some screen readers repeat it.
   */
  #announce(text) {
    const node = this.querySelector('[data-countdown-announce]');
    if (node && node.textContent !== text) node.textContent = text;
  }

  /** Removes the countdown from the page for good. The rest of the section is untouched. */
  #retire() {
    this.#stop();
    this.hidden = true;
    this.remove();
  }

  #render() {
    const remaining = this.#remaining();

    if (remaining <= 0) {
      this.#finish();
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const parts = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };

    // Padded unless this instance opted out. See the note in the file header.
    const pad = this.dataset.pad !== 'off';

    for (const [unit, value] of Object.entries(parts)) {
      const node = this.querySelector(`[data-unit="${unit}"]`);
      if (!node) continue;
      const text = pad ? String(value).padStart(2, '0') : String(value);
      if (node.textContent !== text) node.textContent = text;
    }

    // Once a minute, not once a second. See the note in the file header.
    const minuteStamp = Math.floor(totalSeconds / 60);
    if (minuteStamp !== this.#lastAnnouncedMinute) {
      this.#lastAnnouncedMinute = minuteStamp;
      const template = this.dataset.announceTemplate;
      if (template) {
        this.#announce(
          template
            .replace('{days}', parts.days)
            .replace('{hours}', parts.hours)
            .replace('{minutes}', parts.minutes)
        );
      }
    }
  }
}

if (!customElements.get('mh-countdown')) {
  customElements.define('mh-countdown', MhCountdown);
}
